import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { useDashboardData } from '../data/DashboardDataProvider';
import { chartConfig, setOptions } from '../utils/chartConfigs';
import { colors } from '../utils/colors';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const LS_KEY_CAMPAIGN = 'cac_campaign';
const LS_KEY_CAMPAIGN2 = 'cac_campaign2';
const LS_KEY_BASELINE = 'cac_baseline';
const LS_KEY_LTV = 'cac_ltv';
const LS_KEY_CAMPAIGN_VIEW = 'cac_campaign_view';
const LS_KEY_CAMPAIGN2_VIEW = 'cac_campaign2_view';
const LS_KEY_BASELINE_VIEW = 'cac_baseline_view';
const LS_KEY_INCREMENTAL_VIEW_CAMPAIGN1 = 'cac_incremental_view_campaign1';
const LS_KEY_INCREMENTAL_VIEW_CAMPAIGN2 = 'cac_incremental_view_campaign2';

const NEW_COLOR = '#2f9bff';
const BLENDED_COLOR = '#8a6de9';
const MONEY_COLOR = '#63c17c';
const REACTIVATION_COLOR = colors.darkorange;

function toDateStr(d) {
  return d.toISOString().slice(0, 10);
}

function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return toDateStr(d);
}

function defaultTo() {
  return toDateStr(new Date());
}

function defaultBaselineFrom(campaignFrom) {
  const d = new Date(campaignFrom);
  d.setDate(d.getDate() - 30);
  return toDateStr(d);
}

function defaultBaselineTo(campaignFrom) {
  const d = new Date(campaignFrom);
  d.setDate(d.getDate() - 1);
  return toDateStr(d);
}

function loadFromLS(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveToLS(key, val) {
  localStorage.setItem(key, JSON.stringify(val));
}

function filterEventsByDateRange(events, from, to) {
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime() + 86400000; // inclusive end
  return events.filter(item => {
    const d = item.creation_date;
    return d >= fromMs && d < toMs;
  });
}

function sumPaidSubscriptions(data) {
  return data.reduce((sum, item) => {
    return sum
      + (parseInt(item.subscription_paid_plus, 10) || 0)
      + (parseInt(item.subscription_paid_songwriter, 10) || 0)
      + (parseInt(item.subscription_paid_pro, 10) || 0);
  }, 0);
}

function getDailyPaidData(data) {
  return data.map(item =>
    (parseInt(item.subscription_paid_plus, 10) || 0)
    + (parseInt(item.subscription_paid_songwriter, 10) || 0)
    + (parseInt(item.subscription_paid_pro, 10) || 0)
  );
}

function sumUpgrades(data) {
  return data.reduce((sum, item) => sum + (parseInt(item.subscription_reactivation, 10) || 0), 0);
}

function buildUpgradeLookup(data) {
  const map = {};
  data.forEach(item => {
    const key = new Date(item.creation_date).toISOString().slice(0, 10);
    map[key] = (parseInt(item.subscription_reactivation, 10) || 0);
  });
  return map;
}

function getDailyUpgradeData(flagsFiltered, upgradeLookup) {
  return flagsFiltered.map(item => {
    const key = new Date(item.creation_date).toISOString().slice(0, 10);
    return upgradeLookup[key] || 0;
  });
}

function getDailyLabels(data) {
  return data.map(item => {
    const d = new Date(item.creation_date);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  });
}

function hexToRgba(hex, alpha) {
  const normalized = hex.replace('#', '');
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const inputStyle = { padding: '6px 10px', border: '1px solid #d2d6da', borderRadius: 6, fontSize: 14 };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#344767', marginBottom: 4 };
const rowStyle = { display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 };
const toggleGroupStyle = { display: 'inline-flex', border: '1px solid #d2d6da', borderRadius: 8, overflow: 'hidden' };
const toggleButtonStyle = { padding: '6px 12px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', background: '#fff', color: '#67748e' };
const statBoxStyle = {
  background: '#f8f9fa', borderRadius: 8, padding: '12px 20px', textAlign: 'center', minWidth: 140, border: '1px solid #edf0f2',
};
const statLabel = { fontSize: 12, color: '#7b809a', marginBottom: 4 };
const statValue = { fontSize: 22, fontWeight: 700, color: '#344767' };
const newMetricBoxStyle = {
  ...statBoxStyle,
  background: hexToRgba(NEW_COLOR, 0.12),
  border: `1px solid ${hexToRgba(NEW_COLOR, 0.3)}`,
};
const incrementalNewEmphasisBoxStyle = {
  ...newMetricBoxStyle,
  border: `2px solid ${hexToRgba(NEW_COLOR, 0.65)}`,
};
const blendedMetricBoxStyle = {
  ...statBoxStyle,
  background: hexToRgba(BLENDED_COLOR, 0.12),
  border: `1px solid ${hexToRgba(BLENDED_COLOR, 0.3)}`,
};
const incrementalCombinedEmphasisBoxStyle = {
  ...blendedMetricBoxStyle,
  border: `2px solid ${hexToRgba(BLENDED_COLOR, 0.65)}`,
};
const primaryKpiFocusBoxStyle = {
  ...blendedMetricBoxStyle,
  border: `4px solid ${hexToRgba(BLENDED_COLOR, 0.75)}`,
};
const moneyMetricBoxStyle = {
  ...statBoxStyle,
  background: hexToRgba(MONEY_COLOR, 0.12),
  border: `1px solid ${hexToRgba(MONEY_COLOR, 0.32)}`,
};
const totalMetricBoxStyle = {
  ...blendedMetricBoxStyle,
};
const newPaidBoxStyle = {
  ...newMetricBoxStyle,
};
const reactivationBoxStyle = {
  ...statBoxStyle,
  background: hexToRgba(REACTIVATION_COLOR, 0.12),
  border: `1px solid ${hexToRgba(REACTIVATION_COLOR, 0.3)}`,
};

const PeriodCard = ({ title, description, storageKey, viewStorageKey, defaultView = 'combined', flagsPerDay, analyticsEventsPerDay, errors, defaultFromDate, defaultToDate, defaultCost, ltv, onChange }) => {
  const saved = loadFromLS(storageKey);
  const [from, setFrom] = useState(saved?.from || defaultFromDate || defaultFrom());
  const [to, setTo] = useState(saved?.to || defaultToDate || defaultTo());
  const [cost, setCost] = useState(saved?.cost ?? defaultCost ?? '');
  const [periodView, setPeriodView] = useState(() => loadFromLS(viewStorageKey) || defaultView);
  const isCampaign1 = storageKey === LS_KEY_CAMPAIGN;

  const persist = (f, t, c) => {
    saveToLS(storageKey, { from: f, to: t, cost: c });
    onChange?.({ from: f, to: t, cost: c });
  };

  const filtered = useMemo(
    () => filterEventsByDateRange(flagsPerDay.data || [], from, to),
    [flagsPerDay.data, from, to]
  );
  const eventsFiltered = useMemo(
    () => filterEventsByDateRange(analyticsEventsPerDay.data || [], from, to),
    [analyticsEventsPerDay.data, from, to]
  );

  const paidStarts = sumPaidSubscriptions(filtered);
  const upgrades = sumUpgrades(eventsFiltered);
  const blendedTotal = paidStarts + upgrades;
  const costNum = parseFloat(cost) || 0;
  const newCAC = paidStarts > 0 ? costNum / paidStarts : 0;
  const blendedCAC = blendedTotal > 0 ? costNum / blendedTotal : 0;

  const labels = getDailyLabels(filtered);
  const paidData = getDailyPaidData(filtered);
  const upgradeLookup = useMemo(() => buildUpgradeLookup(eventsFiltered), [eventsFiltered]);
  const upgradeData = getDailyUpgradeData(filtered, upgradeLookup);

  return (
    <ChartCard header={title} loading={flagsPerDay.loading || analyticsEventsPerDay.loading} error={errors.flagsPerDay}
      headerRight={(
        <div style={toggleGroupStyle}>
          <button
            type="button"
            style={{
              ...toggleButtonStyle,
              background: periodView === 'combined' ? hexToRgba(BLENDED_COLOR, 0.16) : '#fff',
              color: periodView === 'combined' ? '#344767' : '#67748e',
            }}
            onClick={() => { setPeriodView('combined'); saveToLS(viewStorageKey, 'combined'); }}
          >
            Combined
          </button>
          <button
            type="button"
            style={{
              ...toggleButtonStyle,
              background: periodView === 'new' ? hexToRgba(NEW_COLOR, 0.16) : '#fff',
              color: periodView === 'new' ? '#344767' : '#67748e',
            }}
            onClick={() => { setPeriodView('new'); saveToLS(viewStorageKey, 'new'); }}
          >
            New Paid Only
          </button>
          <button
            type="button"
            style={{
              ...toggleButtonStyle,
              background: periodView === 'hide' ? '#e9ecef' : '#fff',
              color: periodView === 'hide' ? '#344767' : '#67748e',
            }}
            onClick={() => { setPeriodView('hide'); saveToLS(viewStorageKey, 'hide'); }}
          >
            Hide
          </button>
        </div>
      )}
      description={description} hideAvg hideTotal collapsed={periodView === 'hide'}>
      <div style={rowStyle}>
        <div>
          <div style={labelStyle}>From</div>
          <input type="date" value={from} style={inputStyle}
            onChange={e => { setFrom(e.target.value); persist(e.target.value, to, cost); }} />
        </div>
        <div>
          <div style={labelStyle}>To</div>
          <input type="date" value={to} style={inputStyle}
            onChange={e => { setTo(e.target.value); persist(from, e.target.value, cost); }} />
        </div>
        <div>
          <div style={labelStyle}>Ad Spend (SEK)</div>
          <input type="number" min="0" step="0.01" value={cost} placeholder="0" style={{ ...inputStyle, width: 120 }}
            onChange={e => { setCost(e.target.value); persist(from, to, e.target.value); }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={newPaidBoxStyle}>
          <div style={statLabel}>New Paid</div>
          <div style={statValue}>{paidStarts}</div>
        </div>
        <div style={reactivationBoxStyle}>
          <div style={statLabel}>Reactivations</div>
          <div style={statValue}>{upgrades}</div>
        </div>
        <div style={totalMetricBoxStyle}>
          <div style={statLabel}>Total Combined</div>
          <div style={statValue}>{blendedTotal}</div>
        </div>
        <div style={moneyMetricBoxStyle}>
          <div style={statLabel}>Ad Spend</div>
          <div style={statValue}>{costNum.toFixed(0)} kr</div>
        </div>
        {periodView === 'new' && (
          <div style={newMetricBoxStyle}>
            <div style={statLabel}>New CAC</div>
            <div style={statValue}>{paidStarts > 0 ? `${newCAC.toFixed(0)} kr` : '—'}</div>
          </div>
        )}
        {periodView === 'combined' && (
          <div style={blendedMetricBoxStyle}>
            <div style={statLabel}>CAC</div>
            <div style={statValue}>{blendedTotal > 0 ? `${blendedCAC.toFixed(0)} kr` : '—'}</div>
          </div>
        )}
        {periodView === 'new' && ltv > 0 && newCAC > 0 && (
          <div style={newMetricBoxStyle}>
            <div style={statLabel}>LTV / New CAC</div>
            <div style={statValue}>{(ltv / newCAC).toFixed(1)}x</div>
          </div>
        )}
        {periodView === 'combined' && ltv > 0 && blendedCAC > 0 && (
          <div style={isCampaign1 ? primaryKpiFocusBoxStyle : blendedMetricBoxStyle}>
            <div style={statLabel}>LTV / CAC</div>
            <div style={statValue}>{(ltv / blendedCAC).toFixed(1)}x</div>
          </div>
        )}
      </div>
      {filtered.length > 0 && (
        <div style={{ height: 200 }}>
          <Line data={chartConfig(labels, ['New Paid', 'Reactivations'], [paidData, upgradeData], [NEW_COLOR, REACTIVATION_COLOR], true, true)}
            options={{ ...setOptions(true), maintainAspectRatio: false }} />
        </div>
      )}
    </ChartCard>
  );
};

const CACPage = () => {
  const { analyticsData, errors } = useDashboardData();
  const { flagsPerDay, analyticsEventsPerDay } = analyticsData;
  const [incrementalViewCampaign1, setIncrementalViewCampaign1] = useState(() => loadFromLS(LS_KEY_INCREMENTAL_VIEW_CAMPAIGN1) || 'combined');
  const [incrementalViewCampaign2, setIncrementalViewCampaign2] = useState(() => loadFromLS(LS_KEY_INCREMENTAL_VIEW_CAMPAIGN2) || 'hide');

  const [ltv, setLtv] = useState(() => {
    try { return loadFromLS(LS_KEY_LTV) ?? '950'; } catch { return '950'; }
  });
  const ltvNum = parseFloat(ltv) || 0;

  const initCampaign = loadFromLS(LS_KEY_CAMPAIGN);
  const initCampaign2 = loadFromLS(LS_KEY_CAMPAIGN2);
  const initBaseline = loadFromLS(LS_KEY_BASELINE);

  const [campaignFrom, setCampaignFrom] = useState(initCampaign?.from || '2026-02-19');
  const [campaignTo, setCampaignTo] = useState(initCampaign?.to || '2026-03-31');
  const [campaignCost, setCampaignCost] = useState(parseFloat(initCampaign?.cost) || 20100);

  const [campaign2From, setCampaign2From] = useState(initCampaign2?.from || '2026-03-04');
  const [campaign2To, setCampaign2To] = useState(initCampaign2?.to || '2026-03-31');
  const [campaign2Cost, setCampaign2Cost] = useState(parseFloat(initCampaign2?.cost) || 11500);

  const defBaselineFrom = '2026-01-01';
  const defBaselineTo = '2026-02-01';
  const [baselineFrom, setBaselineFrom] = useState(initBaseline?.from || defBaselineFrom);
  const [baselineTo, setBaselineTo] = useState(initBaseline?.to || defBaselineTo);

  const onCampaignChange = ({ from, to, cost }) => {
    setCampaignFrom(from); setCampaignTo(to); setCampaignCost(parseFloat(cost) || 0);
  };
  const onCampaign2Change = ({ from, to, cost }) => {
    setCampaign2From(from); setCampaign2To(to); setCampaign2Cost(parseFloat(cost) || 0);
  };
  const onBaselineChange = ({ from, to }) => {
    setBaselineFrom(from); setBaselineTo(to);
  };

  // Baseline
  const baselineFiltered = useMemo(
    () => filterEventsByDateRange(flagsPerDay.data || [], baselineFrom, baselineTo),
    [flagsPerDay.data, baselineFrom, baselineTo]
  );
  const baselineEventsFiltered = useMemo(
    () => filterEventsByDateRange(analyticsEventsPerDay.data || [], baselineFrom, baselineTo),
    [analyticsEventsPerDay.data, baselineFrom, baselineTo]
  );
  const baselineNewStarts = sumPaidSubscriptions(baselineFiltered);
  const baselineUpgrades = sumUpgrades(baselineEventsFiltered);
  const baselineBlended = baselineNewStarts + baselineUpgrades;
  const baselineDays = Math.max(1, Math.round((new Date(baselineTo) - new Date(baselineFrom)) / 86400000));
  const baselineNewRate = baselineDays > 0 ? baselineNewStarts / baselineDays : 0;
  const baselineBlendedRate = baselineDays > 0 ? baselineBlended / baselineDays : 0;

  // Helper to compute incremental metrics for a campaign
  function computeIncremental(cFrom, cTo, cCost, flagsData, eventsData) {
    const cFiltered = filterEventsByDateRange(flagsData, cFrom, cTo);
    const cEventsFiltered = filterEventsByDateRange(eventsData, cFrom, cTo);
    const newStarts = sumPaidSubscriptions(cFiltered);
    const upgrades = sumUpgrades(cEventsFiltered);
    const blended = newStarts + upgrades;
    const days = Math.max(1, Math.round((new Date(cTo) - new Date(cFrom)) / 86400000));
    const expectedNew = Math.round(baselineNewRate * days);
    const incrNew = Math.max(0, newStarts - expectedNew);
    const incrNewCAC = incrNew > 0 ? cCost / incrNew : 0;
    const expectedBlended = Math.round(baselineBlendedRate * days);
    const incrBlended = Math.max(0, blended - expectedBlended);
    const incrBlendedCAC = incrBlended > 0 ? cCost / incrBlended : 0;
    return { newStarts, upgrades, blended, days, expectedNew, incrNew, incrNewCAC, expectedBlended, incrBlended, incrBlendedCAC, cost: cCost };
  }

  const c1 = useMemo(() => computeIncremental(campaignFrom, campaignTo, campaignCost, flagsPerDay.data || [], analyticsEventsPerDay.data || []),
    [campaignFrom, campaignTo, campaignCost, flagsPerDay.data, analyticsEventsPerDay.data, baselineNewRate, baselineBlendedRate]);
  const c2 = useMemo(() => computeIncremental(campaign2From, campaign2To, campaign2Cost, flagsPerDay.data || [], analyticsEventsPerDay.data || []),
    [campaign2From, campaign2To, campaign2Cost, flagsPerDay.data, analyticsEventsPerDay.data, baselineNewRate, baselineBlendedRate]);

  return (
    <section>
      <div className="chart-grid">
        <div className="chart-col-full">
          <ChartCard header="LTV Assumption" hideAvg hideTotal
            description="Enter estimated customer lifetime value (SEK). Used to compute LTV/CAC ratios across all cards.">
            <div style={rowStyle}>
              <div>
                <div style={labelStyle}>LTV (SEK)</div>
                <input type="number" min="0" step="1" value={ltv} placeholder="e.g. 500" style={{ ...inputStyle, width: 160 }}
                  onChange={e => { setLtv(e.target.value); saveToLS(LS_KEY_LTV, e.target.value); }} />
              </div>
              {ltvNum > 0 && (
                <div style={{ ...moneyMetricBoxStyle, alignSelf: 'center' }}>
                  <div style={statLabel}>LTV</div>
                  <div style={statValue}>{ltvNum.toFixed(0)} kr</div>
                </div>
              )}
            </div>
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <PeriodCard
            title="Campaign Period 1 — CAC"
            description="New = first-time paid conversions (flags). Reactivations = subscription upgrades (cancelled users returning)."
            storageKey={LS_KEY_CAMPAIGN}
            viewStorageKey={LS_KEY_CAMPAIGN_VIEW}
            defaultView="combined"
            defaultFromDate="2026-02-19"
            defaultToDate="2026-03-31"
            defaultCost="20100"
            flagsPerDay={flagsPerDay}
            analyticsEventsPerDay={analyticsEventsPerDay}
            errors={errors}
            ltv={ltvNum}
            onChange={onCampaignChange}
          />
        </div>

        <div className="chart-col-full">
          <PeriodCard
            title="Campaign Period 2 — CAC"
            description="Second campaign period for comparison. Compares against the same baseline."
            storageKey={LS_KEY_CAMPAIGN2}
            viewStorageKey={LS_KEY_CAMPAIGN2_VIEW}
            defaultView="hide"
            defaultFromDate="2026-03-04"
            defaultToDate="2026-03-31"
            defaultCost="11500"
            flagsPerDay={flagsPerDay}
            analyticsEventsPerDay={analyticsEventsPerDay}
            errors={errors}
            ltv={ltvNum}
            onChange={onCampaign2Change}
          />
        </div>

        <div className="chart-col-full">
          <PeriodCard
            title="Baseline Period (no campaign)"
            description="Define a baseline period (usually with 0 kr spend) to establish the organic paid subscription rate."
            storageKey={LS_KEY_BASELINE}
            viewStorageKey={LS_KEY_BASELINE_VIEW}
            defaultView="hide"
            flagsPerDay={flagsPerDay}
            analyticsEventsPerDay={analyticsEventsPerDay}
            errors={errors}
            defaultFromDate={defBaselineFrom}
            defaultToDate={defBaselineTo}
            ltv={ltvNum}
            onChange={onBaselineChange}
          />
        </div>

        {[[
          'Campaign 1', c1, incrementalViewCampaign1, setIncrementalViewCampaign1, LS_KEY_INCREMENTAL_VIEW_CAMPAIGN1,
        ], [
          'Campaign 2', c2, incrementalViewCampaign2, setIncrementalViewCampaign2, LS_KEY_INCREMENTAL_VIEW_CAMPAIGN2,
        ]].map(([label, c, incrementalView, setIncrementalView, incrementalViewKey]) => (
          <div className="chart-col-full" key={label}>
            <ChartCard header={`Incremental CAC — ${label} vs Baseline`}
              loading={flagsPerDay.loading || analyticsEventsPerDay.loading} error={errors.flagsPerDay}
                description={`Baseline: ${baselineNewRate.toFixed(1)} new/day, ${baselineBlendedRate.toFixed(1)} combined/day (${baselineDays} days).`}
              headerRight={(
                <div style={toggleGroupStyle}>
                  <button
                    type="button"
                    style={{
                      ...toggleButtonStyle,
                      background: incrementalView === 'combined' ? hexToRgba(BLENDED_COLOR, 0.16) : '#fff',
                      color: incrementalView === 'combined' ? '#344767' : '#67748e',
                    }}
                    onClick={() => { setIncrementalView('combined'); saveToLS(incrementalViewKey, 'combined'); }}
                  >
                    Combined
                  </button>
                  <button
                    type="button"
                    style={{
                      ...toggleButtonStyle,
                      background: incrementalView === 'new' ? hexToRgba(NEW_COLOR, 0.16) : '#fff',
                      color: incrementalView === 'new' ? '#344767' : '#67748e',
                    }}
                    onClick={() => { setIncrementalView('new'); saveToLS(incrementalViewKey, 'new'); }}
                  >
                    New Paid Only
                  </button>
                  <button
                    type="button"
                    style={{
                      ...toggleButtonStyle,
                      background: incrementalView === 'hide' ? '#e9ecef' : '#fff',
                      color: incrementalView === 'hide' ? '#344767' : '#67748e',
                    }}
                    onClick={() => { setIncrementalView('hide'); saveToLS(incrementalViewKey, 'hide'); }}
                  >
                    Hide
                  </button>
                </div>
              )}
              hideAvg hideTotal collapsed={incrementalView === 'hide'}>
              {incrementalView === 'new' && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#344767', marginBottom: 8 }}>New Paid Only</div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={newMetricBoxStyle}>
                  <div style={statLabel}>New Paid</div>
                  <div style={statValue}>{c.newStarts}</div>
                </div>
                <div style={statBoxStyle}>
                  <div style={statLabel}>Expected Baseline</div>
                  <div style={statValue}>{c.expectedNew}</div>
                </div>
                <div style={incrementalNewEmphasisBoxStyle}>
                  <div style={statLabel}>Incremental New</div>
                  <div style={statValue}>{c.incrNew}</div>
                </div>
                <div style={newMetricBoxStyle}>
                  <div style={statLabel}>New CAC</div>
                  <div style={statValue}>{c.newStarts > 0 ? `${(c.cost / c.newStarts).toFixed(0)} kr` : '—'}</div>
                </div>
                <div style={incrementalNewEmphasisBoxStyle}>
                  <div style={statLabel}>Incremental New CAC</div>
                  <div style={statValue}>{c.incrNew > 0 ? `${c.incrNewCAC.toFixed(0)} kr` : '—'}</div>
                </div>
                {ltvNum > 0 && c.cost > 0 && c.newStarts > 0 && (
                  <div style={newMetricBoxStyle}>
                    <div style={statLabel}>LTV / New CAC</div>
                    <div style={statValue}>{(ltvNum / (c.cost / c.newStarts)).toFixed(1)}x</div>
                  </div>
                )}
                {ltvNum > 0 && c.cost > 0 && c.incrNew > 0 && (
                  <div style={incrementalNewEmphasisBoxStyle}>
                    <div style={statLabel}>LTV / Incr. New CAC</div>
                    <div style={statValue}>{(ltvNum / c.incrNewCAC).toFixed(1)}x</div>
                  </div>
                )}
                  </div>
                </>
              )}

              {incrementalView === 'combined' && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#344767', marginBottom: 8 }}>New Paid + Reactivations</div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={totalMetricBoxStyle}>
                  <div style={statLabel}>Total</div>
                  <div style={statValue}>{c.blended}</div>
                </div>
                <div style={statBoxStyle}>
                  <div style={statLabel}>Expected Baseline</div>
                  <div style={statValue}>{c.expectedBlended}</div>
                </div>
                <div style={incrementalCombinedEmphasisBoxStyle}>
                  <div style={statLabel}>Incremental</div>
                  <div style={statValue}>{c.incrBlended}</div>
                </div>
                <div style={blendedMetricBoxStyle}>
                  <div style={statLabel}>CAC</div>
                  <div style={statValue}>{c.blended > 0 ? `${(c.cost / c.blended).toFixed(0)} kr` : '—'}</div>
                </div>
                <div style={incrementalCombinedEmphasisBoxStyle}>
                  <div style={statLabel}>Incremental CAC</div>
                  <div style={statValue}>{c.incrBlended > 0 ? `${c.incrBlendedCAC.toFixed(0)} kr` : '—'}</div>
                </div>
                {ltvNum > 0 && c.cost > 0 && c.blended > 0 && (
                  <div style={blendedMetricBoxStyle}>
                    <div style={statLabel}>LTV / CAC</div>
                    <div style={statValue}>{(ltvNum / (c.cost / c.blended)).toFixed(1)}x</div>
                  </div>
                )}
                {ltvNum > 0 && c.cost > 0 && c.incrBlended > 0 && (
                  <div style={label === 'Campaign 1' ? primaryKpiFocusBoxStyle : incrementalCombinedEmphasisBoxStyle}>
                    <div style={statLabel}>LTV / Incr. CAC</div>
                    <div style={statValue}>{(ltvNum / c.incrBlendedCAC).toFixed(1)}x</div>
                  </div>
                )}
                  </div>
                </>
              )}
            </ChartCard>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CACPage;
